import { BufferGeometry, Quaternion, Vector3 } from 'three'
import {
  comprimento,
  multiplicar,
  normalizar,
  somar,
  subtrair,
  type Vetor3,
} from '../../dominio/geometria/vetor3'

/**
 * Emenda em esquadria entre trechos consecutivos de uma barra.
 *
 * Uma coluna é uma polilinha: um arco aproximado por trechos retos. Cada trecho
 * era uma caixa de extremos quadrados e, para não abrir fresta na quebra de
 * direção, estendida um pouco além do ponto; no vinco, a ponta de um trecho
 * atravessava o outro e deixava um degrau. Em esquadria os dois trechos são
 * cortados pelo MESMO plano (a bissetriz das duas direções): as faces de corte
 * coincidem, sem sobreposição e sem fresta.
 */

/** Direção do trecho vizinho em cada extremo, no sentido da polilinha. */
export interface Emendas {
  /** direção do trecho que chega a este (anterior → este) */
  readonly inicio?: Vetor3
  /** direção do trecho que sai deste (este → seguinte) */
  readonly fim?: Vetor3
  /**
   * Normal (no mundo) do plano que corta um extremo LIVRE, sem vizinho: o pé e o
   * topo de uma coluna terminam rente ao elemento que encontram (a soleira, o
   * teto), em vez de em ponta inclinada. Sem ele o extremo livre é perpendicular
   * ao eixo e estende a folga.
   */
  readonly planoInicio?: Vetor3
  readonly planoFim?: Vetor3
  /**
   * Quanto, em metros, o plano de corte do extremo final fica acima do ponto
   * final, medido ao longo da normal do plano. O trecho se estende o suficiente
   * para alcançá-lo. É o que faz o topo de uma coluna chegar ao topo do
   * travessão do teto, que passa do ponto de eixo.
   */
  readonly elevacaoPlanoFim?: number
}

/**
 * Normal do plano de corte entre duas direções consecutivas, ou `null` quando a
 * barra volta sobre si mesma e não há plano que sirva.
 */
export function normalDoPlanoDeEmenda(anterior: Vetor3, seguinte: Vetor3): Vetor3 | null {
  const soma = somar(normalizar(anterior), normalizar(seguinte))
  return comprimento(soma) < 1e-6 ? null : normalizar(soma)
}

/**
 * Emendas de cada trecho de uma polilinha: só os extremos livres ficam sem. Os
 * dois extremos da polilinha inteira podem receber um plano de corte.
 */
export function emendasDaPolilinha(
  pontos: readonly Vetor3[],
  planos: { inicio?: Vetor3; fim?: Vetor3; elevacaoFim?: number } = {},
): Emendas[] {
  const trechos = pontos.length - 1
  const direcao = (i: number) => normalizar(subtrair(pontos[i + 1]!, pontos[i]!))
  return Array.from({ length: Math.max(0, trechos) }, (_, i) => ({
    inicio: i > 0 ? direcao(i - 1) : undefined,
    fim: i < trechos - 1 ? direcao(i + 1) : undefined,
    planoInicio: i === 0 ? planos.inicio : undefined,
    planoFim: i === trechos - 1 ? planos.fim : undefined,
    elevacaoPlanoFim: i === trechos - 1 ? planos.elevacaoFim : undefined,
  }))
}

export interface PlanoDoTrecho {
  /** comprimento total do trecho, já com a folga dos extremos livres */
  readonly tamanho: number
  /** centro do trecho no mundo (desloca quando só um extremo estende) */
  readonly centro: Vetor3
  /** normal do corte de cada extremo, nas coordenadas locais do trecho (eixo Y ao longo da barra) */
  readonly corteInicio: Vector3 | null
  readonly corteFim: Vector3 | null
}

/**
 * Dimensões de um trecho entre `de` e `para`. `folga` estende só os extremos
 * LIVRES, metade em cada: um extremo em esquadria encosta exatamente no vizinho.
 */
export function planejarTrecho(
  de: Vetor3,
  para: Vetor3,
  folga: number,
  emendas: Emendas | undefined,
  orientacao: Quaternion,
): PlanoDoTrecho {
  const delta = subtrair(para, de)
  const comprimentoBase = comprimento(delta)
  const direcao = comprimentoBase < 1e-9 ? { x: 0, y: 1, z: 0 } : normalizar(delta)

  const normalInicio = emendas?.inicio
    ? normalDoPlanoDeEmenda(emendas.inicio, direcao)
    : emendas?.planoInicio ? normalizar(emendas.planoInicio) : null
  const normalFim = emendas?.fim
    ? normalDoPlanoDeEmenda(direcao, emendas.fim)
    : emendas?.planoFim ? normalizar(emendas.planoFim) : null

  const extensaoInicio = normalInicio ? 0 : folga / 2
  // um extremo em esquadria encosta no vizinho; um com plano explícito só se
  // estende até a elevação do plano, medida ao longo da normal
  const cosPlanoFim = normalFim && !emendas?.fim ? Math.max(0.2, direcao.x * normalFim.x + direcao.y * normalFim.y + direcao.z * normalFim.z) : 1
  const extensaoFim = normalFim ? (emendas?.fim ? 0 : (emendas?.elevacaoPlanoFim ?? 0) / cosPlanoFim) : folga / 2

  const inverso = orientacao.clone().invert()
  const local = (n: Vetor3 | null) =>
    n ? new Vector3(n.x, n.y, n.z).applyQuaternion(inverso).normalize() : null

  return {
    tamanho: comprimentoBase + extensaoInicio + extensaoFim,
    centro: somar(
      somar(de, multiplicar(delta, 0.5)),
      multiplicar(direcao, (extensaoFim - extensaoInicio) / 2),
    ),
    corteInicio: local(normalInicio),
    corteFim: local(normalFim),
  }
}

/** Tolerância para reconhecer um vértice como pertencente a um extremo. */
const EPS_EXTREMO = 1e-6

export interface OpcoesDeCorte {
  /**
   * Faixa junto a cada extremo cujos vértices acompanham o corte. Uma tampa tem
   * espessura: sem levar a face de dentro junto, o corte inclinado a inverteria.
   */
  readonly faixaDoExtremo?: number
}

/**
 * Corta os extremos da geometria (barra ao longo de Y, de −tamanho/2 a +tamanho/2)
 * pelos planos de emenda. Desloca em Y só os vértices dos extremos, em função de
 * onde estão na seção, e leva junto a normal das faces de topo. A geometria
 * recebida é alterada e devolvida.
 */
export function cortarExtremos(
  geometria: BufferGeometry,
  tamanho: number,
  corteInicio: Vector3 | null,
  corteFim: Vector3 | null,
  { faixaDoExtremo = 0 }: OpcoesDeCorte = {},
): BufferGeometry {
  if (!corteInicio && !corteFim) return geometria

  const posicao = geometria.attributes.position!
  const normais = geometria.attributes.normal
  const metade = tamanho / 2
  const faixa = Math.max(EPS_EXTREMO, faixaDoExtremo + EPS_EXTREMO)

  for (let i = 0; i < posicao.count; i++) {
    const y = posicao.getY(i)
    const noFim = corteFim !== null && y > metade - faixa
    const noInicio = corteInicio !== null && y < -metade + faixa
    if (!noFim && !noInicio) continue

    const corte = noFim ? corteFim! : corteInicio!
    const extremo = noFim ? metade : -metade
    // plano: n · (p − (0, extremo, 0)) = 0  →  y = extremo − (nx·x + nz·z) / ny
    posicao.setY(i, extremo - (corte.x * posicao.getX(i) + corte.z * posicao.getZ(i)) / corte.y)

    // a normal das faces de topo acompanha o plano; as das paredes seguem como estavam
    if (normais && Math.abs(normais.getY(i)) > 0.999 && Math.abs(Math.abs(y) - metade) < faixa) {
      // a face de fora e a de dentro de uma tampa têm normais opostas: cada uma
      // conserva o sentido que tinha
      const sinal = Math.sign(normais.getY(i))
      normais.setXYZ(i, sinal * corte.x, sinal * corte.y, sinal * corte.z)
    }
  }

  posicao.needsUpdate = true
  if (normais) normais.needsUpdate = true
  geometria.computeBoundingBox()
  geometria.computeBoundingSphere()
  return geometria
}
